// ***** Dependencies *****
import React, { ErrorInfo, ReactNode } from "react";

// ***** Components *****
import ErrorFallback from "./error-fallback";

interface ErrorBoundaryProps {
    children: ReactNode;
    /** Rendered instead of `children` once an error has been caught. */
    fallback?: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Catches render errors anywhere below it, including failures to load a
 * `React.lazy` chunk, which would otherwise blank the whole application.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("ErrorBoundary", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? <ErrorFallback />;
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
